/* eslint-disable react/no-unknown-property */
import React, { useCallback, useMemo } from 'react';
import Image from '@/components/common/SpiritoryImage';
import useTypedTranslation from '@/hooks/useTypedTranslation';
import { Col } from 'react-bootstrap';
import Container from '@/components/Container';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay } from 'swiper/modules';
import { LandingPageProps } from '@/types/props/components/skus';
import CategoryNav from '@/components/home/categoryNav';
import { BlogEnum } from '@/types/translations/BlogEnum';
import IndexNav from '@/components/home/Index/IndexNav/IndexNav';
import checkArray from '@/utilites/check-array';
import { buildStrapiImageUrl } from '@/utilites/strapi.utils';
import { useQueries } from '@tanstack/react-query';
import getNewSkus from '@/api/getNewSkus';
import getReleaseCalendar from '@/api/getReleaseCalendar';
import getCollectionsUseQuery from '@/api/getCollectionsUseQuery';
import { useRouter } from 'next/router';
import { SkuDTO } from '@/types/api/sku';
import { FAQSection } from './FAQSection';
import { LatestArticles } from './LatestArticles';

// Helper function for preparing query data
const prepareQueryData = <T,>(data: any): T[] => {
  return (checkArray(data) ? data : []) as T[];
};

// Shared React Query config to reduce repetition
const QUERY_CONFIG = {
  staleTime: 5 * 60 * 1000,
  refetchOnWindowFocus: false,
  retry: 1,
};

export default function LandingPage({
  landingPage: page,
  children,
  hideGoldLine,
  afterContent,
}: LandingPageProps) {
  const { locale } = useRouter();

  const { translate: translateBlog } = useTypedTranslation<BlogEnum>('blog');

  const selectSkus = useCallback((data: any) => prepareQueryData<SkuDTO>(data), []);

  const [collectionsQuery, newSkusQuery, releaseCalendarQuery] = useQueries({
    queries: [
      {
        ...QUERY_CONFIG,
        queryKey: ['collections', 'all', locale],
        queryFn: getCollectionsUseQuery,
      },
      {
        ...QUERY_CONFIG,
        queryKey: ['newSkus'],
        queryFn: getNewSkus,
        select: selectSkus,
      },
      {
        ...QUERY_CONFIG,
        queryKey: ['releaseCalendar'],
        queryFn: getReleaseCalendar,
        select: selectSkus,
      },
    ],
  });

  const newCollections = useMemo(() => {
    const collections = collectionsQuery.data || [];
    const _newCollections = collections.sort((a, b) => {
      const dateA = new Date(a.createdAt || '');
      const dateB = new Date(b.createdAt || '');
      return dateB.getTime() - dateA.getTime();
    });
    const newCollections = _newCollections.slice(0, 5);
    return newCollections.map(collection => ({
      name: collection?.name || '',
      href: `/collections/${collection?.slug}`,
    }));
  }, [collectionsQuery.data]);

  const newSkus = useMemo(() => {
    const skus = newSkusQuery.data || [];
    const _newSkus = skus.slice(0, 5).map(sku => ({
      name: sku?.name || '',
      href: `/${sku.slug}`,
    }));
    return _newSkus;
  }, [newSkusQuery.data]);

  return (
    <>
      <style jsx global>
        {`
          .brands_content_content h1 {
            font-size: 2rem;
            font-weight: 400;
          }

          .brands_content_content h2 {
            font-size: 30px;
            font-weight: 500;
          }

          .brands_content_content h3 {
            font-size: 22px;
            font-weight: 500;
            color: #03181c !important;
          }

          .brands_content_content h4 {
            font-size: 16px;
            font-weight: 600;
          }

          .brands_content_content p {
            font-weight: 300;
            font-size: 16px;
            margin-bottom: 1rem;
            line-height: 1.75;
            color: black;
          }

          .brands_content_content strong {
            font-weight: 500;
          }

          .gold_line {
            position: relative;
            padding-left: 25px;
            margin-bottom: 2rem;
          }

          .gold_line::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            height: 100%;
            width: 5px;
            background-color: var(--gold);
            border-radius: 8px;
          }

          .content_content h2,
          .content_content h3,
          .content_content h4,
          .content_content h5 {
            color: black !important;
            padding-bottom: 0.4rem;
          }

          .content_content h2 {
            margin-bottom: 2rem;
          }

          .content_content p + div + h2,
          .content_content p + div + h3 {
            margin-top: 1.5rem;
          }

          .content_content h2 + .gold_line {
            margin-top: 0.5rem;
          }

          .content_content h3 + .gold_line {
            margin-top: 5px;
          }

          .brands_content_content p:has(img) {
            color: #5f6d7e !important;
            display: flex;
            flex-direction: column;
            margin-bottom: 2rem;
          }

          .brands_content_content p:has(img) img {
            width: fit-content;
            border-radius: 20px;
            margin-bottom: 10px;
            max-width: 100%;
          }

          .content_first_para h2 {
            margin-bottom: 2rem;
          }

          .h2_gold_line:after {
            content: '';
            display: block;
            background-color: rgb(194, 173, 125);
            border-radius: 20px;
            max-width: 350px;
            height: 5px;
            margin-top: 10px;
          }

          a .brands_content_content h2:after {
            content: none;
          }

          .gold_line h2,
          .gold_line h3,
          .gold_line h4,
          .gold_line h5 {
            padding-bottom: 6px;
          }

          .brands_content_content a {
            color: black;
          }

          .table_content_content ul {
            padding-left: 15px;
            color: var(--blue);
            display: flex;
            flex-wrap: wrap;
          }

          .table_content_content ul li {
            padding-bottom: 10px;
            font-size: 14px;
            flex: 1 1 50%;
          }

          .table_content_content ul li a {
            color: var(--darkblue);
          }

          .brands_content_content a {
            color: var(--darkblue);
          }

          /* Accordion */

          .faqsection .accordion-item:first-child .accordion-button {
            border-top: none;
          }

          .faqsection .accordion-button {
            background-color: #ededee80;
            border: none;
            border-top: 1px solid #919ba7;
            text-shadow: none;
            margin-left: 0;
            padding-left: 0;
          }

          .faqsection .accordion-body {
            background-color: #ededee80;
            padding-left: 0;
          }

          .faqsection .accordion-item {
            border: none;
          }

          .faqsection .accordion-button:not(.collapsed) {
            background-color: #ededee80;
            box-shadow: none;
          }

          /* Black Friday 2025 */

          .blackfriday2025 .table_content_content {
            display: none;
          }

          .blackfriday2025 h1 {
            visibility: hidden;
          }

          @media only screen and (max-width: 1000px) {
            .table_content_content ul {
              flex-direction: column;
            }

            .brands_content_content img {
              width: 100%;
              max-width: 100%;
            }

            .related_blogs_div .tw-flex.tw-gap-8 {
              flex-direction: column;
            }
          }
        `}
      </style>

      <Container>
        <Col xs={12} className="max-nine:!tw-hidden">
          <IndexNav
            newSkus={newSkus}
            upcomingReleases={releaseCalendarQuery.data || []}
            newCollections={newCollections}
          />
        </Col>
      </Container>
      <div className="tw-w-full tw-bg-white tw-font-inter tw-text-text tw-antialiased max-nine:tw-mt-2">
        <Container>
          {/* Hero Section with Overlay */}
          <div className="tw-relative tw-h-[285px] tw-w-full tw-overflow-hidden tw-rounded-3xl max-nine:tw-h-[150px]">
            <Swiper
              slidesPerView={1}
              loop={true}
              autoplay={{
                delay: 5000,
                disableOnInteraction: false,
              }}
              modules={[Autoplay]}
              className="tw-h-full tw-w-full tw-rounded-3xl"
            >
              {(page?.banner_image || page?.banner_image_mobile) && (
                <SwiperSlide
                  key={JSON.stringify(page?.banner_image || page?.banner_image_mobile)}
                  className="tw-relative tw-h-full tw-w-full tw-rounded-3xl"
                >
                  {/* Mobile banner image: shown up to breakpoint nine (< 900px) */}
                  {page?.banner_image_mobile && (
                    <Image
                      src={buildStrapiImageUrl(page.banner_image_mobile)}
                      alt={page?.name_string || ''}
                      width={1325}
                      height={285}
                      priority
                      className="tw-absolute tw-inset-0 tw-z-10 tw-w-full tw-rounded-3xl tw-object-cover tw-object-center tw-transition-transform hover:tw-scale-105 max-nine:tw-block nine:tw-hidden max-nine:!tw-object-contain max-nine:!tw-h-[150px]"
                    />
                  )}
                  {/* Desktop banner image: shown for breakpoint nine and larger (>= 900px) */}
                  {page?.banner_image && (
                    <Image
                      src={buildStrapiImageUrl(page.banner_image)}
                      alt={page?.name_string || ''}
                      width={1325}
                      height={285}
                      priority
                      className="tw-absolute tw-inset-0 tw-z-10 tw-w-full tw-rounded-3xl tw-object-cover tw-object-center tw-transition-transform hover:tw-scale-105 max-nine:tw-hidden nine:tw-block nine:tw-aspect-[1325/285]"
                    />
                  )}
                </SwiperSlide>
              )}
            </Swiper>

            {/* Content Overlay */}
            <div className="tw-absolute tw-inset-0 tw-z-20 tw-flex tw-items-center">
              <div className="tw-w-full tw-px-4">
                <div
                  className={`tw-text-white max-nine:tw-mx-auto max-nine:tw-max-w-[90%] max-nine:tw-text-center nine:tw-ml-[50px] nine:tw-max-w-[650px] nine:tw-pl-[3vw]`}
                >
                  {page?.banner_headline ? (
                    <h1
                      className={`tw-font-prata tw-text-2xl tw-font-normal !tw-leading-[1.3] sm:tw-text-3xl md:tw-text-4xl lg:tw-text-5xl max-nine:tw-mb-0`}
                      dangerouslySetInnerHTML={{
                        __html: page.banner_headline || '',
                      }}
                    ></h1>
                  ) : page?.name_string ? (
                    // No headline means the banner image carries the text, so the page had
                    // no H1 at all. Name it for search engines and screen readers without
                    // printing over the artwork (WEB-1639).
                    <h1 className="tw-sr-only">{page.name_string}</h1>
                  ) : null}
                  {/* The line underlines the banner copy; without any it would cut
                      across an image that carries its own lettering (WEB-1639). */}
                  {!hideGoldLine && (page?.banner_headline || page?.banner_text) && (
                    <hr
                      className={`tw-my-4 max-nine:tw-my-2 tw-h-1 tw-w-[250px] tw-rounded-full tw-border-none tw-bg-custom-gold tw-opacity-100 max-nine:tw-mx-auto sm:tw-w-[350px]`}
                    />
                  )}
                  {page?.banner_text ? (
                    <h2
                      className={`tw-text-base tw-font-medium tw-leading-relaxed tw-text-[#DEDFE2] sm:tw-text-lg sm:tw-leading-[33px] md:tw-text-[18px]`}
                      dangerouslySetInnerHTML={{
                        __html: page.banner_text || '',
                      }}
                    ></h2>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </Container>

        <div className="tw-my-8 max-nine:tw-my-4">{children}</div>

        <div className={'brands_content_box'}>
          <LatestArticles />

          <Container className="brands_content_content">
            <Col className={`mx-auto tw-mt-[60px]`} md={8}>
              <div
                className={`content_first_para`}
                dangerouslySetInnerHTML={{
                  __html: page?.content_first_para || '',
                }}
              ></div>
              <div
                className={`table_content_content tw-my-[30px] tw-rounded-[20px] tw-bg-customGray tw-p-[30px]`}
              >
                <h3 className={`tw-pb-[20px]`}> {translateBlog(BlogEnum.content_table)}</h3>

                <div
                  dangerouslySetInnerHTML={{
                    __html: page?.content_table || '',
                  }}
                ></div>
              </div>
              <div
                className={`content_content`}
                dangerouslySetInnerHTML={{
                  __html: page?.content_text || '',
                }}
              ></div>
            </Col>
          </Container>
        </div>

        {afterContent}
      </div>

      <FAQSection faqs={page?.faqs} />
    </>
  );
}
